import apiClient from '../lib/apiClient';
import localAuthService from '../../ERP/src/services/localAuthService';

// Sign in with email and password
const signIn = async (email, password) => {
  try {
    if (!email || !password) {
      return { success: false, error: 'Email and password are required' };
    }

    const result = await apiClient.signIn(email.trim().toLowerCase(), password);

    if (result.success) {
      // Get user profile
      if (result.data?.session?.user?.id) {
        const profileCheck = await getUserProfile(result.data.session.user.id);
        if (profileCheck.success) {
          result.userProfile = profileCheck.data;
        }
      }

      if (result.userProfile?.role) {
      const role = result.userProfile.role;
      let dashboardRoute = '/erp/dashboard-overview';

      switch (role) {
        case 'super_admin':
          dashboardRoute = '/erp/dashboard-overview';
          break;
        case 'hr_admin':
          dashboardRoute = '/erp/employee-management';
          break;
        case 'telecaller':
          dashboardRoute = '/erp/telecaller-dashboard';
          break;
        case 'staff':
          dashboardRoute = '/erp/client-requirements-matching';
          break;
        case 'client':
          dashboardRoute = '/erp/invoice-billing-management';
          break;
        case 'employee':
          dashboardRoute = '/erp/employee-self-service';
          break;
        default:
          dashboardRoute = '/erp/dashboard-overview';
      }

        result.dashboardRoute = dashboardRoute;
      }

      return result;
    }

    const localFallback = await localAuthService.signIn(email, password);
    if (localFallback?.success) {
      return localFallback;
    }

    return { 
      success: false, 
      error: result.error || 'Invalid email or password. Please check your credentials and try again.' 
    };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch') || 
        error?.message?.includes('Cannot connect to API server')) {
      const localFallback = await localAuthService.signIn(email, password);
      if (localFallback?.success) {
        return localFallback;
      }
      return { 
        success: false, 
        error: 'Cannot connect to API server. Please check if the backend is running.' 
      };
    }
    
    const localFallback = await localAuthService.signIn(email, password);
    if (localFallback?.success) {
      return localFallback;
    }

    return { success: false, error: error.message || 'Authentication service temporarily unavailable. Please try again later.' }
  }
}

// Sign up with email, password and user data
const signUp = async (email, password, userData = {}) => {
  try {
    const result = await apiClient.post('/auth/register', {
      email: email.trim().toLowerCase(),
      password,
      full_name: userData.full_name || '',
      role: userData.role || 'employee'
    });

    return result;
  } catch (error) {
    return { 
      success: false, 
      error: error.message || 'Registration service temporarily unavailable. Please try again later.' 
    }
  }
}

// Sign out
const signOut = async () => {
  try {
    const result = await apiClient.signOut();
    await localAuthService.signOut();
    return { success: true };
  } catch (error) {
    // Always succeed on logout, even if API call fails
    await localAuthService.signOut();
    return { success: true };
  }
}

// Get current session
const getSession = async () => {
  try {
    const localSession = await localAuthService.getSession();
    if (localSession?.data?.session) {
      return localSession;
    }
    const result = await apiClient.getSession();
    return result;
  } catch (error) {
    const localSession = await localAuthService.getSession();
    if (localSession?.data?.session) {
      return localSession;
    }
    return {
      success: false,
      data: { session: null },
      error: error.message,
    };
  }
}

// Get user profile
const getUserProfile = async (userId) => {
  try {
    const localProfile = await localAuthService.getUserProfile(userId);
    if (localProfile?.success) {
      return localProfile;
    }
    const result = await apiClient.getUserProfile(userId);
    return result;
  } catch (error) {
    const localProfile = await localAuthService.getUserProfile(userId);
    if (localProfile?.success) {
      return localProfile;
    }
    return {
      success: false,
      error: error.message,
    };
  }
}

// Reset password
const resetPassword = async (email) => {
  try {
    const result = await apiClient.resetPassword(email);
    return result;
  } catch (error) {
    const localReset = await localAuthService.resetPassword(email);
    if (localReset?.success) {
      return localReset;
    }
    return {
      success: false,
      error: error.message || 'Password reset service temporarily unavailable.',
    };
  }
}

// Auth state change listener (simplified version)
const onAuthStateChange = (callback) => {
  return localAuthService.onAuthStateChange(callback);
}

const authService = {
  signIn,
  signUp,
  signOut,
  getSession,
  getUserProfile,
  resetPassword,
  onAuthStateChange,
  getDemoCredentials: localAuthService.getDemoCredentials
};

export default authService;

